import {
  SessionServerSelectors,
  SessionSiteSelectors,
  SessionUserSelectors
} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';

import { canPerformAnyAction } from '../selectors/ResourceActionsSelectors';

/**
 * Check if an entity action is clickable based on resource-level actions.
 * When isAdminProfilesEnabledOnServer is ON, uses canPerformAnyAction.
 * When admin profiles is OFF, returns the fallback for backward compatibility.
 *
 * @param state - Redux AppState
 * @param resourceType - e.g. 'User', 'Group', etc.
 * @param resourceActions - Action names that permit this operation (e.g. 'SEE_SITE_USERS', 'MODIFY_USERS')
 * @param fallback - Value when admin profiles is off (backward compatibility )
 */
export function isResourceActionClickable(
  state: AppState,
  resourceType: string,
  resourceActions: string[],
  fallback: boolean
): boolean {
  if (!SessionServerSelectors.isAdminProfilesEnabledOnServer(state)) {
    return fallback;
  }
  return canPerformAnyAction(state, resourceType, resourceActions);
}

/**
 * Resolves whether an entity action is clickable, applying resource-permission checks
 * when the action has a resourcePermission config (set in ActionsList.tsx on IEntityActionListEntry
 * and populated in getActionSectionsForItems).
 *
 * Logic:
 * - Site/Server Admin: Always use action's native isClickable (admins have full access)
 * - resourcePermission present:
 *   - Admin profiles off: native isClickable only (legacy)
 *   - Admin profiles on: canPerformAnyAction for resource AND native isClickable (item rules such as
 *     "cannot edit All Users group" live in native isClickable)
 * - resourcePermission absent: Use native isClickable
 */
export function resolveEntityActionClickable(
  action: {
    resourcePermission?: { resourceType: string, resourceActions: string[] };
    isClickable: (availableActions: any, targetItems: any[], state: AppState) => boolean;
  },
  availableActions: any,
  targetItems: any[],
  state: AppState
): boolean {

  // Site/Server admins always use native isClickable
  const isSiteAdmin = SessionSiteSelectors.isSessionSiteAdmin(state);
  const isServerAdmin = SessionUserSelectors.isSessionServerAdmin(state);
  if (isSiteAdmin || isServerAdmin) {
    return action.isClickable(availableActions, targetItems, state);
  }

  if (action.resourcePermission) {
    const fallback = action.isClickable(availableActions, targetItems, state);
    if (!SessionServerSelectors.isAdminProfilesEnabledOnServer(state)) {
      return fallback;
    }
    return canPerformAnyAction(
      state,
      action.resourcePermission.resourceType,
      action.resourcePermission.resourceActions
    ) && fallback;
  }
  // no resourcePermission: use native isClickable
  return action.isClickable(availableActions, targetItems, state);
}

/**
 * Resolves whether an entity action is visible, applying resource-permission checks
 * when the action has a resourcePermission config and isAdminProfilesEnabledOnServer is ON.
 *
 * When isAdminProfilesEnabledOnServer is ON and the action has resourcePermission:
 * - Site/Server Admin: Always visible if native isVisible passes
 * - Non-admin: Hidden if user lacks any of the required resource actions
 *
 * When isAdminProfilesEnabledOnServer is OFF or no resourcePermission: uses native isVisible only.
 */
export function resolveEntityActionVisible(
  action: {
    resourcePermission?: { resourceType: string, resourceActions: string[] };
    isVisible: (targetItems: any[], state: AppState, availableActions?: any, sourceComponent?: any) => boolean;
  },
  targetItems: any[],
  state: AppState,
  availableActions?: any,
  sourceComponent?: any
): boolean {
  if (!action.isVisible(targetItems, state, availableActions, sourceComponent)) {
    return false;
  }

  if (!SessionServerSelectors.isAdminProfilesEnabledOnServer(state) || !action.resourcePermission) {
    return true;
  }

  const isSiteAdmin = SessionSiteSelectors.isSessionSiteAdmin(state);
  const isServerAdmin = SessionUserSelectors.isSessionServerAdmin(state);
  if (isSiteAdmin || isServerAdmin) {
    return true;
  }

  return canPerformAnyAction(
    state,
    action.resourcePermission.resourceType,
    action.resourcePermission.resourceActions
  );
}
